import * as Sentry from '@sentry/react';
import AppMain from 'App';
import RouteGuard from 'components/route-guard/RouteGuard';
import BootstrapRouteWrapper from 'components/shared/BootstrapRouteWrapper';
import MainFrame from 'layouts/mainframe/MainFrame';
import CrossLoginPage from 'pages/cross-login/CrossLoginPage';
import DashboardPage from 'pages/dashboard/DashboardPage';
import { ExitIframe } from 'pages/ExitIframe';
import LoginLandingPage from 'pages/LoginLandingPage';
import NotFoundPage from 'pages/NotFoundPage';
import { RedirectOutsideIframe } from 'pages/RedirectOutsideIframe';
import ServerErrorPage from 'pages/ServerErrorPage';
import React from 'react';
import {
    createBrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    redirect,
    useLocation,
    useNavigationType,
} from 'react-router';

Sentry.init({
    dsn: 'https://e6e6129c0556658d4cf9528407057fdb@o4507724573638656.ingest.us.sentry.io/4509133148848128',
    integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
    ],
    environment: import.meta.env.VITE_APP_ENV,
    enabled: ['production', 'development'].includes(import.meta.env.VITE_APP_ENV),
    tracesSampleRate: 1.0,
    enableLogs: true,
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
    {
        Component: AppMain,
        children: [
            {
                Component: MainFrame,
                ErrorBoundary: ServerErrorPage,
                children: [
                    {
                        Component: BootstrapRouteWrapper,
                        children: [
                            {
                                Component: RouteGuard,
                                children: [
                                    {
                                        index: true,
                                        Component: DashboardPage,
                                    },
                                    {
                                        path: 'sse-test',
                                        lazy: async () => {
                                            const { default: Component } = await import('pages/sse-test/SseTestPage');
                                            return {
                                                Component,
                                            };
                                        },
                                    },
                                    {
                                        path: 'billing',
                                        children: [
                                            {
                                                index: true,
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/billing/CurrentSubscriptionPage');
                                                    return {
                                                        Component,
                                                    };
                                                },
                                            },
                                            {
                                                path: 'thank-you',
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/billing/BillingThankYouAppPricingPage');
                                                    return {
                                                        Component,
                                                    };
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        path: 'order-details-redirect',
                                        loader: ({ request }) => {
                                            const id = new URL(request.url).searchParams.get('id');
                                            return redirect(id ? `/orders/${id}` : '/orders');
                                        },
                                    },
                                    {
                                        path: 'apps',
                                        children: [
                                            {
                                                index: true,
                                                lazy: async () => {
                                                    const { default: Component } = await import('pages/apps/AppsPage');
                                                    return { Component };
                                                },
                                            },
                                            {
                                                path: 'otp',
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/apps/OtpAppPage');
                                                    return { Component };
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        path: 'orders',
                                        children: [
                                            {
                                                index: true,
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/ppoints/orders/OrdersPage');
                                                    return {
                                                        Component,
                                                    };
                                                },
                                            },
                                            {
                                                path: 'redirect',
                                                loader: ({ request }) => {
                                                    const id = new URL(request.url).searchParams.get('id');
                                                    return redirect(id ? `/orders/${id}` : '/orders');
                                                },
                                            },
                                            {
                                                path: '*',
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/ppoints/orders/OrderViewPage');
                                                    return {
                                                        Component,
                                                    };
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        path: 'setting',
                                        children: [
                                            {
                                                index: true,
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/settings/SettingsIndexPage');
                                                    return {
                                                        Component,
                                                    };
                                                },
                                            },
                                            {
                                                path: 'shipping-zones',
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/ppoints/shipping-zones-and-rates/ShippingZonesPage');
                                                    return {
                                                        Component,
                                                    };
                                                },
                                            },
                                            {
                                                path: 'payment-mapping',
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/ppoints/payment-mapping/PaymentMappingPage');
                                                    return {
                                                        Component,
                                                    };
                                                },
                                            },
                                            {
                                                path: 'profile',
                                                lazy: async () => {
                                                    const { default: Component } =
                                                        await import('pages/settings/SettingsPage');
                                                    return {
                                                        Component,
                                                    };
                                                },
                                            },
                                            {
                                                path: 'shipping-providers',
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: async () => {
                                                            const { default: Component } =
                                                                await import('pages/ppoints/shipping-providers/ShippingProvidersPage');
                                                            return {
                                                                Component,
                                                            };
                                                        },
                                                    },
                                                    {
                                                        path: ':providerName/:country',
                                                        lazy: async () => {
                                                            const { default: Component } =
                                                                await import('pages/ppoints/shipping-providers/shipping-provider/ShippingProviderPage');
                                                            return {
                                                                Component,
                                                            };
                                                        },
                                                        // Each settings section is its own sub-page rendered into
                                                        // the provider/aggregator page's <Outlet />. The path segments
                                                        // must stay in sync with the ProviderSettingsSection enum.
                                                        children: [
                                                            {
                                                                index: true,
                                                                lazy: async () => {
                                                                    const { SectionIndexRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'connect',
                                                                lazy: async () => {
                                                                    const { ConnectionSectionRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'general',
                                                                lazy: async () => {
                                                                    const { GeneralSectionRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'sender',
                                                                lazy: async () => {
                                                                    const { SenderSectionRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'shipping',
                                                                lazy: async () => {
                                                                    const { ShippingSectionRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'pickup-points',
                                                                lazy: async () => {
                                                                    const { PickupPointsRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'home-delivery',
                                                                lazy: async () => {
                                                                    const { HomeDeliveryRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'rules',
                                                                lazy: async () => {
                                                                    const { RulesSectionRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'order-attributes',
                                                                lazy: async () => {
                                                                    const { OrderAttributesSectionRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                            {
                                                                path: 'service-providers',
                                                                lazy: async () => {
                                                                    const { ServiceProvidersSectionRoute: Component } =
                                                                        await import('features/settings/components/ppoints/shipping-provider/provider-settings-pages/section-routes');
                                                                    return {
                                                                        Component,
                                                                    };
                                                                },
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'integration-mode',
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: async () => {
                                                            const { default: Component } =
                                                                await import('pages/ppoints/integration-and-design/IntegrationModePage');
                                                            return {
                                                                Component,
                                                            };
                                                        },
                                                    },
                                                    {
                                                        path: 'design-settings',
                                                        lazy: async () => {
                                                            const { default: Component } =
                                                                await import('pages/ppoints/integration-and-design/cart-map-settings/DesignSettingsPage');
                                                            return {
                                                                Component,
                                                            };
                                                        },
                                                    },
                                                    {
                                                        path: 'checkout-settings',
                                                        lazy: async () => {
                                                            const { default: Component } =
                                                                await import('pages/ppoints/integration-and-design/checkout-list-settings/CheckoutSettingsPage');
                                                            return {
                                                                Component,
                                                            };
                                                        },
                                                    },
                                                    {
                                                        path: 'checkout-map-settings',
                                                        lazy: async () => {
                                                            const { default: Component } =
                                                                await import('pages/ppoints/integration-and-design/checkout-map-settings/CheckoutMapSettingsPage');
                                                            return {
                                                                Component,
                                                            };
                                                        },
                                                    },
                                                    {
                                                        path: 'after-checkout-map-settings',
                                                        lazy: async () => {
                                                            const { default: Component } =
                                                                await import('pages/ppoints/integration-and-design/after-checkout-map-settings/AfterCheckoutMapSettingsPage');
                                                            return {
                                                                Component,
                                                            };
                                                        },
                                                    },
                                                    {
                                                        path: 'smart-delivery-page-settings',
                                                        lazy: async () => {
                                                            const { default: Component } =
                                                                await import('pages/ppoints/integration-and-design/smart-delivery-page-settings/SmartDeliveryPageSettingsPage');
                                                            return {
                                                                Component,
                                                            };
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: 'crosslogin',
                Component: CrossLoginPage,
            },
            {
                path: 'login-landing',
                Component: LoginLandingPage,
            },
            {
                path: 'server-error',
                Component: ServerErrorPage,
            },
            {
                path: 'ExitIframe',
                Component: ExitIframe,
            },
            {
                path: 'RedirectOutsideIframe',
                Component: RedirectOutsideIframe,
            },
            {
                path: '*',
                Component: NotFoundPage,
            },
        ],
    },
]);

export default router;
